import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth, ensureAnalytics } from './firebase';
import Header from './components/Header';
import Footer from './components/Footer';
import ConsentBanner from './components/ConsentBanner';
import ScrollToHash from './components/ScrollToHash';
import { usePageView, useScrollDepth, useTimeOnPage } from './analytics/hooks';

// Code splitting : chaque page est chargée à la demande (chunk séparé) pour
// alléger le bundle initial et accélérer le premier affichage.
const HomePage = lazy(() => import('./pages/HomePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const CGUPage = lazy(() => import('./pages/CGUPage'));
const CGVPage = lazy(() => import('./pages/CGVPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const PrivacyPolicyDriverPage = lazy(() => import('./pages/PrivacyPolicyDriverPage'));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const DriverShowcasePage = lazy(() => import('./pages/DriverShowcasePage'));
const DriverLoginPage = lazy(() => import('./pages/DriverLoginPage'));
const DriverSpacePage = lazy(() => import('./pages/DriverSpacePage'));
const MobilePageWrapper = lazy(() => import('./pages/MobilePage'));
const GuideMonite = lazy(() => import('./components/GuideMonite'));
const ChyprePage = lazy(() => import('./pages/ChyprePage'));
const YoniversePage = lazy(() => import('./pages/YoniversePage'));

function PageLoader() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loading-spinner"></div>
    </div>
  );
}

function GlobalAnalytics() {
  React.useEffect(() => { ensureAnalytics(); }, []);
  usePageView();
  useScrollDepth();
  useTimeOnPage();
  return null;
}

function ProtectedRoute({ children }) {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div style={{ minHeight: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) return <Navigate to="/driver-space/login" replace />;
  return children;
}

const WithChrome = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

export default function App() {
  return (
    <Router>
      <GlobalAnalytics />
      <ScrollToHash />
      <ConsentBanner />
      <div className="App">
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/mobile" element={<MobilePageWrapper />} />
          <Route path="/" element={<WithChrome><HomePage /></WithChrome>} />
          <Route path="/faq" element={<WithChrome><FAQPage /></WithChrome>} />
          <Route path="/chypre" element={<WithChrome><ChyprePage /></WithChrome>} />
          <Route path="/yoniverse" element={<><YoniversePage /><Footer /></>} />
          <Route path="/conditions-generales-dutilisation" element={<WithChrome><CGUPage /></WithChrome>} />
          <Route path="/conditions-generales-de-vente-internet" element={<WithChrome><CGVPage /></WithChrome>} />
          <Route path="/politique-de-confidentialite" element={<WithChrome><PrivacyPolicyPage /></WithChrome>} />
          <Route path="/politique-de-confidentialite-driver" element={<WithChrome><PrivacyPolicyDriverPage /></WithChrome>} />
          <Route path="/supprimer-mon-compte" element={<WithChrome><AccountDeletionPage /></WithChrome>} />
          <Route path="/driver-showcase" element={<WithChrome><DriverShowcasePage /></WithChrome>} />
          <Route path="/guide-monite" element={<GuideMonite />} />
          <Route path="/driver-space/login" element={<DriverLoginPage />} />
          <Route path="/driver-space" element={<ProtectedRoute><DriverSpacePage /></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </div>
    </Router>
  );
}
